/**
 * Advertiser Console — /dashboard/advertiser (+ /admin/advertiser)
 *
 * Phase 2, step 3: the self-serve "buy ads" console. Writes to the adv_* tables via
 * the dataAccess layer (localStorage-or-API), mirroring the Roles console. Billing is a
 * MOCK balance here (the ledger-charger / budget-sweeper jobs + real payment are step 4+).
 *
 * Tabs: Account · Wallet · Campaigns · Creatives · Reports.
 * Reports aggregates are computed client-side from the loaded adv_impression/adv_click
 * rows (aggregate-only — no per-user data), so it works in both API and localStorage modes.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiPlus, FiDollarSign, FiTarget, FiImage, FiBarChart2, FiUser } from 'react-icons/fi';
import { loadTable, getTableData, addRow, saveRow } from '../db-viewer/data/dataAccess';
import { restoreSession } from '../../lib/session';
import { setFormDirty, confirmDiscardChanges } from '../../lib/unsavedChanges';
import './AdvertiserConsole.css';

// adv_* primary-key columns (guessIdColumn can't derive these — pass explicitly).
const PK = {
  adv_advertiser: 'advertiser_id',
  adv_billing_ledger: 'ledger_id',
  adv_campaign: 'campaign_id',
  adv_creative: 'creative_id',
  adv_campaign_placement: 'campaign_placement_id',
  adv_campaign_creative: 'campaign_creative_id',
  adv_placement: 'placement_id',
} as const;

const AD_ORIGIN = process.env.REACT_APP_AD_ORIGIN || 'http://localhost:5000';
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const ALLOWED_MIME = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
const MAX_KB = 300;

type Row = Record<string, any>;
type Tab = 'account' | 'wallet' | 'campaigns' | 'creatives' | 'reports';

const TABS: { key: Tab; label: string; icon: React.ReactNode }[] = [
  { key: 'account', label: 'Account', icon: <FiUser /> },
  { key: 'wallet', label: 'Wallet', icon: <FiDollarSign /> },
  { key: 'campaigns', label: 'Campaigns', icon: <FiTarget /> },
  { key: 'creatives', label: 'Creatives', icon: <FiImage /> },
  { key: 'reports', label: 'Reports', icon: <FiBarChart2 /> },
];

// eslint-disable-next-line eqeqeq
const eq = (a: any, b: any) => a == b;
const notDeleted = (r: Row) => !eq(r.is_deleted, 1);
const money = (n: number) => '$' + (Number(n) || 0).toFixed(2);

function computeBalance(ledger: Row[]): number {
  return ledger.reduce((b, r) => {
    const amt = Number(r.amount) || 0;
    if (r.entry_type === 'deposit' || r.entry_type === 'refund' || r.entry_type === 'credit') return b + amt;
    if (r.entry_type === 'charge') return b - amt;
    if (r.entry_type === 'adjustment') return b + amt; // signed by convention
    return b;
  }, 0);
}

const StatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <span className={`adv-badge adv-badge-${status}`}>{status}</span>
);

export const AdvertiserConsolePage: React.FC = () => {
  const session = restoreSession();
  const userId = session ? parseInt(session.id, 10) : 0;

  const [tab, setTab] = useState<Tab>('account');
  // Bumped to remount (reset to landing) when the active tab is re-clicked.
  const [tabResetKey, setTabResetKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [advertiser, setAdvertiser] = useState<Row | null>(null);
  const [ledger, setLedger] = useState<Row[]>([]);
  const [campaigns, setCampaigns] = useState<Row[]>([]);
  const [creatives, setCreatives] = useState<Row[]>([]);
  const [placements, setPlacements] = useState<Row[]>([]);
  const [campCreatives, setCampCreatives] = useState<Row[]>([]);
  const [impressions, setImpressions] = useState<Row[]>([]);
  const [clicks, setClicks] = useState<Row[]>([]);
  const [notice, setNotice] = useState<string>('');

  const reload = useCallback(async () => {
    await Promise.all([
      loadTable('adv_advertiser'), loadTable('adv_billing_ledger'), loadTable('adv_campaign'),
      loadTable('adv_creative'), loadTable('adv_placement'), loadTable('adv_campaign_creative'),
      loadTable('adv_impression'), loadTable('adv_click'),
    ]);
    const mine = getTableData('adv_advertiser').filter(notDeleted)
      .find(a => eq(a.user_id, userId) && !eq(a.is_house, 1)) || null;
    setAdvertiser(mine);
    const advId = mine ? mine.advertiser_id : -1;
    setLedger(getTableData('adv_billing_ledger').filter(notDeleted).filter(r => eq(r.advertiser_id, advId)));
    setCampaigns(getTableData('adv_campaign').filter(notDeleted).filter(r => eq(r.advertiser_id, advId)));
    setCreatives(getTableData('adv_creative').filter(notDeleted).filter(r => eq(r.advertiser_id, advId)));
    setPlacements(getTableData('adv_placement').filter(notDeleted).filter(r => eq(r.is_enabled, 1)));
    setCampCreatives(getTableData('adv_campaign_creative').filter(notDeleted));
    setImpressions(getTableData('adv_impression').filter(notDeleted));
    setClicks(getTableData('adv_click').filter(notDeleted));
    setLoading(false);
  }, [userId]);

  useEffect(() => { reload(); }, [reload]);

  const balance = useMemo(() => computeBalance(ledger), [ledger]);
  const flash = (msg: string) => { setNotice(msg); setTimeout(() => setNotice(''), 3500); };

  const switchTab = async (next: Tab) => {
    if (!(await confirmDiscardChanges())) return;
    setFormDirty(false);
    // Re-clicking the active tab resets it to its landing (remount); otherwise switch.
    if (next === tab) setTabResetKey(k => k + 1);
    else setTab(next);
  };

  if (loading) return <div className="adv-wrap"><p className="adv-muted">Loading advertiser console…</p></div>;

  return (
    <div className="adv-wrap">
      <header className="adv-header">
        <div>
          <h2><FiTarget /> Advertise on Akountant</h2>
          <p className="adv-muted">Reach free-tier listeners with self-hosted, privacy-safe ads.</p>
        </div>
        {advertiser && (
          <div className="adv-acct-chip">
            <span>{advertiser.advertiser_name}</span>
            <StatusBadge status={advertiser.status} />
            <strong>{money(balance)}</strong>
          </div>
        )}
      </header>

      {notice && <div className="adv-notice">{notice}</div>}

      <nav className="adv-tabs">
        {TABS.map(t => (
          <button
            key={t.key}
            className={`adv-tab ${tab === t.key ? 'active' : ''}`}
            onClick={() => switchTab(t.key)}
            disabled={!advertiser && t.key !== 'account'}
            title={!advertiser && t.key !== 'account' ? 'Create your advertiser profile first' : ''}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </nav>

      <section className="adv-panel">
        <React.Fragment key={`${tab}-${tabResetKey}`}>
        {tab === 'account' && (
          <AccountTab advertiser={advertiser} userId={userId} onSaved={(m) => { flash(m); reload(); }} />
        )}
        {tab === 'wallet' && advertiser && (
          <WalletTab advertiser={advertiser} ledger={ledger} balance={balance}
            onSaved={(m) => { flash(m); reload(); }} />
        )}
        {tab === 'campaigns' && advertiser && (
          <CampaignsTab advertiser={advertiser} campaigns={campaigns} creatives={creatives}
            placements={placements} campCreatives={campCreatives}
            onSaved={(m) => { flash(m); reload(); }} />
        )}
        {tab === 'creatives' && advertiser && (
          <CreativesTab advertiser={advertiser} creatives={creatives}
            onSaved={(m) => { flash(m); reload(); }} />
        )}
        {tab === 'reports' && advertiser && (
          <ReportsTab campaigns={campaigns} impressions={impressions} clicks={clicks} />
        )}
        </React.Fragment>
      </section>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Account tab — become an advertiser / edit profile
// ---------------------------------------------------------------------------
const AccountTab: React.FC<{ advertiser: Row | null; userId: number; onSaved: (m: string) => void }> = ({ advertiser, userId, onSaved }) => {
  const [form, setForm] = useState({
    advertiser_name: advertiser?.advertiser_name || '',
    legal_name: advertiser?.legal_name || '',
    contact_email: advertiser?.contact_email || '',
    website_url: advertiser?.website_url || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k: string, v: string) => { setForm(f => ({ ...f, [k]: v })); setFormDirty(true); };

  const submit = async () => {
    if (!form.advertiser_name.trim()) { onSaved('Advertiser name is required.'); return; }
    setSaving(true);
    try {
      if (advertiser) {
        await saveRow('adv_advertiser', advertiser.advertiser_id, { ...form }, PK.adv_advertiser);
        onSaved('Profile updated.');
      } else {
        await addRow('adv_advertiser', {
          user_id: userId, ...form, advertiser_type: 'self_serve', is_house: 0,
          account_balance: 0, currency: 'USD', status: 'pending', is_active: 1, is_deleted: 0,
        }, PK.adv_advertiser);
        onSaved('Advertiser profile created — pending approval. You can build campaigns now.');
      }
      setFormDirty(false);
    } finally { setSaving(false); }
  };

  return (
    <div className="adv-form-card">
      <h3>{advertiser ? 'Advertiser profile' : 'Become an advertiser'}</h3>
      {!advertiser && <p className="adv-muted">Create a profile to start building campaigns. New accounts are reviewed before ads go live.</p>}
      {advertiser && <p className="adv-muted">Status: <StatusBadge status={advertiser.status} /></p>}
      <label>Advertiser name *<input value={form.advertiser_name} onChange={e => set('advertiser_name', e.target.value)} placeholder="Acme Vinyl Co." /></label>
      <label>Legal name<input value={form.legal_name} onChange={e => set('legal_name', e.target.value)} /></label>
      <label>Contact email<input value={form.contact_email} onChange={e => set('contact_email', e.target.value)} placeholder="ads@acme.com" /></label>
      <label>Website<input value={form.website_url} onChange={e => set('website_url', e.target.value)} placeholder="https://acme.com" /></label>
      <button className="adv-btn-primary" onClick={submit} disabled={saving}>
        {advertiser ? 'Save profile' : 'Create advertiser profile'}
      </button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Wallet tab — mock balance + ledger
// ---------------------------------------------------------------------------
const WalletTab: React.FC<{ advertiser: Row; ledger: Row[]; balance: number; onSaved: (m: string) => void }> = ({ advertiser, ledger, balance, onSaved }) => {
  const [amount, setAmount] = useState('50');
  const [busy, setBusy] = useState(false);

  const addFunds = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { onSaved('Enter a positive amount.'); return; }
    setBusy(true);
    try {
      const token = localStorage.getItem('akt_auth_token');
      const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
      const res = await fetch(`${API_BASE}/ads/deposit`, {
        method: 'POST', headers,
        body: JSON.stringify({ advertiser_id: advertiser.advertiser_id, amount: amt }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        onSaved(e.error || (res.status === 401 ? 'Log in to the database first.' : 'Deposit failed.'));
        return;
      }
      const j = await res.json();
      if (j.url) { window.location.href = j.url; return; } // live → redirect to Stripe Checkout
      // dev-mock → confirm the deposit immediately
      const c = await fetch(`${API_BASE}/ads/deposit/confirm`, { method: 'POST', headers, body: JSON.stringify({ ref: j.ref }) });
      if (!c.ok) { const e = await c.json().catch(() => ({})); onSaved(e.error || 'Confirm failed.'); return; }
      onSaved(`Added ${money(amt)} (test mode).`);
    } catch {
      onSaved('Deposit needs the backend API.');
    } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="adv-balance-card">
        <div><span className="adv-muted">Current balance</span><div className="adv-balance">{money(balance)}</div></div>
        <div className="adv-addfunds">
          <input type="number" min="1" value={amount} onChange={e => setAmount(e.target.value)} />
          <button className="adv-btn-primary" onClick={addFunds} disabled={busy}><FiPlus /> Add funds</button>
        </div>
      </div>
      <p className="adv-muted adv-mock-note">Test mode — deposits are simulated (no real charge). Set <code>STRIPE_SECRET_KEY</code> on the server to take live payments via Stripe Checkout.</p>
      <h4>Ledger</h4>
      {ledger.length === 0 ? <p className="adv-muted">No transactions yet.</p> : (
        <table className="adv-table">
          <thead><tr><th>Type</th><th>Amount</th><th>Balance after</th><th>Reference</th></tr></thead>
          <tbody>
            {ledger.slice().reverse().map(r => (
              <tr key={r.ledger_id}>
                <td><span className={`adv-badge adv-badge-${r.entry_type}`}>{r.entry_type}</span></td>
                <td>{money(r.amount)}</td><td>{money(r.balance_after)}</td>
                <td className="adv-muted">{r.reference}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Campaigns tab
// ---------------------------------------------------------------------------
const BLANK_CAMPAIGN = {
  campaign_name: '', objective: 'awareness', pricing_model: 'cpm',
  bid_amount: '2.00', budget_total: '100', start_date: '', end_date: '',
};

const CampaignsTab: React.FC<{
  advertiser: Row; campaigns: Row[]; creatives: Row[]; placements: Row[]; campCreatives: Row[];
  onSaved: (m: string) => void;
}> = ({ advertiser, campaigns, creatives, placements, campCreatives, onSaved }) => {
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ ...BLANK_CAMPAIGN });
  const [busy, setBusy] = useState(false);
  const musicPlacement = placements.find(p => p.placement_code === 'music_list_interstitial');
  const set = (k: string, v: string) => { setForm(f => ({ ...f, [k]: v })); setFormDirty(true); };

  const createCampaign = async () => {
    if (!form.campaign_name.trim()) { onSaved('Campaign name is required.'); return; }
    setBusy(true);
    try {
      const created = await addRow('adv_campaign', {
        advertiser_id: advertiser.advertiser_id, campaign_name: form.campaign_name,
        objective: form.objective, pricing_model: form.pricing_model,
        bid_amount: parseFloat(form.bid_amount) || 0, budget_total: parseFloat(form.budget_total) || 0,
        spend_to_date: 0, currency: advertiser.currency || 'USD',
        start_date: form.start_date || null, end_date: form.end_date || null,
        status: 'draft', is_active: 1, is_deleted: 0,
      }, PK.adv_campaign);
      // Auto-attach the music interstitial placement (the only consumer slot for now).
      if (musicPlacement) {
        await addRow('adv_campaign_placement', {
          campaign_id: created.campaign_id, placement_id: musicPlacement.placement_id,
          targeting: null, is_active: 1, is_deleted: 0,
        }, PK.adv_campaign_placement);
      }
      setForm({ ...BLANK_CAMPAIGN }); setCreating(false); setFormDirty(false);
      onSaved('Campaign created as draft.');
    } finally { setBusy(false); }
  };

  const setStatus = async (c: Row, status: string) => {
    await saveRow('adv_campaign', c.campaign_id, { status }, PK.adv_campaign);
    onSaved(`Campaign "${c.campaign_name}" → ${status}.`);
  };

  const toggleCreative = async (c: Row, cr: Row, attached: boolean) => {
    if (attached) {
      const link = campCreatives.find(l => eq(l.campaign_id, c.campaign_id) && eq(l.creative_id, cr.creative_id));
      if (link) await saveRow('adv_campaign_creative', link.campaign_creative_id, { is_deleted: 1 }, PK.adv_campaign_creative);
    } else {
      await addRow('adv_campaign_creative', {
        campaign_id: c.campaign_id, creative_id: cr.creative_id, rotation_weight: 100, is_active: 1, is_deleted: 0,
      }, PK.adv_campaign_creative);
    }
    onSaved('Creative rotation updated.');
  };

  const attachedCount = (c: Row) => campCreatives.filter(l => eq(l.campaign_id, c.campaign_id)).length;
  const canActivate = (c: Row) => attachedCount(c) > 0;

  return (
    <div>
      <div className="adv-row-between">
        <h3>Campaigns</h3>
        {!creating && <button className="adv-btn-primary" onClick={() => setCreating(true)}><FiPlus /> New campaign</button>}
      </div>

      {creating && (
        <div className="adv-form-card">
          <h4>New campaign</h4>
          <label>Name *<input value={form.campaign_name} onChange={e => set('campaign_name', e.target.value)} placeholder="Spring vinyl push" /></label>
          <div className="adv-grid2">
            <label>Objective
              <select value={form.objective} onChange={e => set('objective', e.target.value)}>
                <option value="awareness">Awareness</option><option value="traffic">Traffic</option><option value="conversions">Conversions</option>
              </select>
            </label>
            <label>Pricing
              <select value={form.pricing_model} onChange={e => set('pricing_model', e.target.value)}>
                <option value="cpm">CPM (per 1000 views)</option><option value="cpc">CPC (per click)</option><option value="flat">Flat</option>
              </select>
            </label>
            <label>Bid ({form.pricing_model.toUpperCase()})<input type="number" step="0.01" value={form.bid_amount} onChange={e => set('bid_amount', e.target.value)} /></label>
            <label>Total budget<input type="number" step="1" value={form.budget_total} onChange={e => set('budget_total', e.target.value)} /></label>
            <label>Start<input type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)} /></label>
            <label>End<input type="date" value={form.end_date} onChange={e => set('end_date', e.target.value)} /></label>
          </div>
          <p className="adv-muted">Placement: Music list interstitial (the only consumer slot today).</p>
          <div className="adv-actions">
            <button className="adv-btn-primary" onClick={createCampaign} disabled={busy}>Create draft</button>
            <button className="adv-btn-ghost" onClick={() => { setCreating(false); setForm({ ...BLANK_CAMPAIGN }); setFormDirty(false); }}>Cancel</button>
          </div>
        </div>
      )}

      {campaigns.length === 0 && !creating ? <p className="adv-muted">No campaigns yet.</p> : (
        campaigns.map(c => (
          <div key={c.campaign_id} className="adv-campaign-card">
            <div className="adv-row-between">
              <div>
                <strong>{c.campaign_name}</strong> <StatusBadge status={c.status} />
                <div className="adv-muted adv-small">
                  {String(c.pricing_model).toUpperCase()} @ {money(c.bid_amount)} · budget {money(c.budget_total)} · spent {money(c.spend_to_date)} · {attachedCount(c)} creative(s)
                </div>
              </div>
              <div className="adv-actions">
                {c.status === 'draft' && <button className="adv-btn-sm" disabled={!canActivate(c)} title={canActivate(c) ? '' : 'Attach a creative first'} onClick={() => setStatus(c, 'pending')}>Submit for review</button>}
                {c.status === 'pending' && <button className="adv-btn-sm" disabled={!canActivate(c)} onClick={() => setStatus(c, 'active')}>Activate</button>}
                {c.status === 'active' && <button className="adv-btn-sm" onClick={() => setStatus(c, 'paused')}>Pause</button>}
                {c.status === 'paused' && <button className="adv-btn-sm" onClick={() => setStatus(c, 'active')}>Resume</button>}
              </div>
            </div>
            <div className="adv-creative-chips">
              {creatives.length === 0 ? <span className="adv-muted adv-small">No creatives — add one in the Creatives tab.</span> :
                creatives.map(cr => {
                  const attached = campCreatives.some(l => eq(l.campaign_id, c.campaign_id) && eq(l.creative_id, cr.creative_id));
                  return (
                    <button key={cr.creative_id} className={`adv-chip ${attached ? 'on' : ''}`} onClick={() => toggleCreative(c, cr, attached)}>
                      {attached ? '✓ ' : '+ '}{cr.creative_name}
                      {!eq(cr.is_approved, 1) && <span className="adv-chip-pending"> (in review)</span>}
                    </button>
                  );
                })}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Creatives tab
// ---------------------------------------------------------------------------
interface PickedImage { base64: string; mime: string; w: number; h: number; kb: number; preview: string; }

const hasImage = (cr: Row) => cr.storage_path === 'db' || String(cr.storage_path || '').startsWith('house:');

const CreativesTab: React.FC<{ advertiser: Row; creatives: Row[]; onSaved: (m: string) => void }> = ({ advertiser, creatives, onSaved }) => {
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [clickUrl, setClickUrl] = useState('');
  const [alt, setAlt] = useState('');
  const [img, setImg] = useState<PickedImage | null>(null);
  const [busy, setBusy] = useState(false);

  const reset = () => { setCreating(false); setName(''); setClickUrl(''); setAlt(''); setImg(null); setFormDirty(false); };

  const onFile = (file?: File) => {
    if (!file) return;
    if (!ALLOWED_MIME.includes(file.type)) { onSaved('Use a PNG, JPEG, GIF or WebP image.'); return; }
    if (file.size > MAX_KB * 1024) { onSaved(`Image too large (max ${MAX_KB} KB).`); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result);
      const base64 = dataUrl.split(',')[1] || '';
      const probe = new window.Image();
      probe.onload = () => {
        setImg({ base64, mime: file.type, w: probe.naturalWidth, h: probe.naturalHeight, kb: Math.round(file.size / 1024), preview: dataUrl });
        setFormDirty(true);
      };
      probe.src = dataUrl;
    };
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!name.trim() || !clickUrl.trim()) { onSaved('Name and click URL are required.'); return; }
    if (!/^https:\/\//i.test(clickUrl)) { onSaved('Click URL must start with https://'); return; }
    if (!img) { onSaved('Choose an image to upload.'); return; }
    setBusy(true);
    try {
      const token = localStorage.getItem('akt_auth_token');
      const res = await fetch(`${API_BASE}/ads/creative`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          advertiser_id: advertiser.advertiser_id, creative_name: name, click_url: clickUrl, alt_text: alt,
          width_px: img.w, height_px: img.h, image_base64: img.base64, image_mime: img.mime,
        }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        onSaved(e.error || (res.status === 401 ? 'Log in to the database to upload images.' : 'Upload failed.'));
        return;
      }
      reset();
      onSaved('Creative uploaded — pending moderation.');
    } catch {
      onSaved('Image upload needs the backend API (images are stored server-side).');
    } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="adv-row-between">
        <h3>Creatives</h3>
        {!creating && <button className="adv-btn-primary" onClick={() => setCreating(true)}><FiPlus /> New creative</button>}
      </div>
      {creating && (
        <div className="adv-form-card">
          <h4>New creative</h4>
          <label>Name *<input value={name} onChange={e => { setName(e.target.value); setFormDirty(true); }} placeholder="Spring banner 728×90" /></label>
          <label>Click URL * (https)<input value={clickUrl} onChange={e => { setClickUrl(e.target.value); setFormDirty(true); }} placeholder="https://acme.com/sale" /></label>
          <label>Alt text<input value={alt} onChange={e => { setAlt(e.target.value); setFormDirty(true); }} /></label>
          <label>Image * (PNG/JPEG/GIF/WebP, max {MAX_KB} KB)
            <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" onChange={e => onFile(e.target.files?.[0])} />
          </label>
          {img && (
            <div className="adv-upload-preview">
              <img src={img.preview} alt="preview" />
              <span className="adv-muted adv-small">{img.w}×{img.h} · {img.kb} KB</span>
            </div>
          )}
          <p className="adv-muted">Images are stored first-party (on our own origin) and served only from there — never hot-linked.</p>
          <div className="adv-actions">
            <button className="adv-btn-primary" onClick={submit} disabled={busy}>Upload creative</button>
            <button className="adv-btn-ghost" onClick={reset}>Cancel</button>
          </div>
        </div>
      )}
      {creatives.length === 0 && !creating ? <p className="adv-muted">No creatives yet.</p> : (
        <table className="adv-table">
          <thead><tr><th>Preview</th><th>Name</th><th>Size</th><th>Click URL</th><th>Status</th></tr></thead>
          <tbody>
            {creatives.map(cr => (
              <tr key={cr.creative_id}>
                <td>{hasImage(cr)
                  ? <img className="adv-thumb" src={`${AD_ORIGIN}/ad/creative/${cr.creative_id}.img`} alt={cr.creative_name} />
                  : <span className="adv-muted adv-small">—</span>}</td>
                <td>{cr.creative_name}</td>
                <td>{cr.width_px}×{cr.height_px}</td>
                <td className="adv-muted adv-truncate">{cr.click_url}</td>
                <td>
                  <span className={`adv-badge adv-badge-${cr.moderation_status}`}>{cr.moderation_status}</span>
                  {cr.moderation_status === 'rejected' && cr.rejection_reason && (
                    <div className="adv-url-bad adv-small" style={{ marginTop: 4 }}>{cr.rejection_reason}</div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Reports tab — client-side aggregation (aggregate-only, no per-user data)
// ---------------------------------------------------------------------------
const ReportsTab: React.FC<{ campaigns: Row[]; impressions: Row[]; clicks: Row[] }> = ({ campaigns, impressions, clicks }) => {
  const rows = campaigns.map(c => {
    const imp = impressions.filter(i => eq(i.campaign_id, c.campaign_id)).length;
    const clk = clicks.filter(k => eq(k.campaign_id, c.campaign_id) && eq(k.is_valid, 1)).length;
    const ctr = imp > 0 ? Math.round((10000 * clk) / imp) / 100 : 0;
    return { name: c.campaign_name, imp, clk, ctr, spend: Number(c.spend_to_date) || 0 };
  });
  const totals = rows.reduce((a, r) => ({ imp: a.imp + r.imp, clk: a.clk + r.clk, spend: a.spend + r.spend }), { imp: 0, clk: 0, spend: 0 });
  const totalCtr = totals.imp > 0 ? Math.round((10000 * totals.clk) / totals.imp) / 100 : 0;

  return (
    <div>
      <h3>Performance</h3>
      <p className="adv-muted">Aggregate impressions &amp; clicks — no per-user data is shown.</p>
      <table className="adv-table">
        <thead><tr><th>Campaign</th><th>Impressions</th><th>Clicks</th><th>CTR</th><th>Spend</th></tr></thead>
        <tbody>
          {rows.length === 0 ? <tr><td colSpan={5} className="adv-muted">No campaigns yet.</td></tr> :
            rows.map((r, i) => (
              <tr key={i}><td>{r.name}</td><td>{r.imp}</td><td>{r.clk}</td><td>{r.ctr}%</td><td>{money(r.spend)}</td></tr>
            ))}
        </tbody>
        {rows.length > 0 && (
          <tfoot><tr><th>Total</th><th>{totals.imp}</th><th>{totals.clk}</th><th>{totalCtr}%</th><th>{money(totals.spend)}</th></tr></tfoot>
        )}
      </table>
    </div>
  );
};

export default AdvertiserConsolePage;
